import type { Demand } from 'types/buyer'
import { Fragment, useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { addParamToPath, formatDate, getElement, toCurrency, formatZipCode } from 'utils'
import { useStore } from 'store'
import copy from 'copy.json'
import { paths, adminPanelPaths } from 'paths'

import Button from 'components/Button'
import Status from 'components/Status'
import Spinner from 'components/Spinner'
import DeleteButton from 'components/DeleteButton'
import ButtonLink from 'components/ButtonLink'
import ConfirmationWarnModal from 'modals/ConfirmationWarnModal'
import DeleteResourceModal from 'modals/DeleteResourceModal'
import StatusHistoryModal from 'modals/StatusHistoryModal'
import { DownloadIcon, InfoIcon, PencilIcon, PlusIcon, PowerIcon } from 'icons'
import EditDemandDetailsModal from './EditDemandDetailsModal'
import ConfirmationEditResourceModal from 'modals/ConfirmationEditResourceModal'
import ConsumptionDetailsModal from 'modals/ConsumptionDetailsModal'
import DetailsWrapper from 'components/layout/DetailsWrapper'
import Detail from 'components/layout/Detail'
import UpdateProfileFileModal from 'modals/UpdateProfileFileModal'

type Props = {
  demand: Demand
}

const form_texts = copy.common.form
const texts = copy.buyer.demand_details
const button = copy.common.button
const profile_file_text = copy.modal.profile_file

export default function DemandDetailsRow({ demand, ...props }: Props) {
  const { buyerId } = useParams()
  const { api, searchDemands } = useStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [expandDetails, setExpandDetails] = useState(false)
  const [demandDetails, setDemandDetails] = useState<Demand | null>(null)
  const [turnOffDemandModalOpen, setTurnOffDemandModalOpen] = useState(false)
  const [turnOffDemandReasonModalOpen, setTurnOffDemandReasonModalOpen] = useState(false)
  const [deleteDemandModalOpen, setDeleteDemandModalOpen] = useState(false)
  const [deleteDemandReasonModalOpen, setDeleteDemandReasonModalOpen] = useState(false)
  const [editDemandDetailsModalOpen, setEditDemandDetailsModalOpen] = useState(false)
  const [confirmationEditModal, setConfirmationEditModal] = useState(false)
  const [statusHistoryModalOpen, setStatusHistoryModalOpen] = useState(false)
  const [consumptionDetailsModalOpen, setConsumptionDetailsModalOpen] = useState(false)
  const [updateProfileFileModalOpen, setUpdateProfileFileModalOpen] = useState(false)
  const { matching, active_offers, file_upload, format_choice } = paths

  useEffect(() => {
    if (Number(location.state) === demand.id) {
      handleDemandDetailsClick(demand.id)
      getElement(demand.id.toString())?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [location.state, demand.id]) // eslint-disable-line

  const getDemandDetails = async (demandId: number) => {
    setLoading(true)

    const response = await api.getResource('demands', demandId)
    setDemandDetails(response)
    setLoading(false)
  }

  const handleDemandActivation = async (
    demandId: number,
    purpose: 'activate' | 'deactivate',
    reason?: string
  ) => {
    const actionReason = purpose === 'activate' ? 'Aktywacja zapotrzebowania' : reason
    const success = await api.activationResource('demand', demandId, purpose, actionReason)

    if (!success) return false

    handleDemandDataRefresh(demandId)
    return true
  }

  const handleDemandDetailsClick = (demandId: number) => {
    if (!expandDetails) {
      getDemandDetails(demandId)
    }

    setExpandDetails(current => !current)
  }

  const handleDownloadProfileFile = (
    profileFileType: 'original' | 'processed',
    demandId: number
  ) => {
    api.getProfileFiles('demands', profileFileType, demandId)
  }

  const handleDemandDelete = async (demandId: number, reason: string) => {
    const success = await api.deleteResource('demands', demandId, reason)

    if (!success) return false

    handleDemandDataRefresh(demandId)
    return true
  }

  const handleEditDemandClick = (hasProcessedProfileFile: boolean) => {
    if (hasProcessedProfileFile) setConfirmationEditModal(true)
    else setEditDemandDetailsModalOpen(true)
  }

  const handleUpdateProfileFile = async (
    demandId: number,
    profileFile: File | null,
    reason: string
  ) => {
    const requestData = { reason: reason }
    const formData = new FormData()
    formData.append(
      'changeReasonRequest',
      new Blob([JSON.stringify(requestData)], { type: 'application/json' })
    )
    if (profileFile) formData.append('profileFile', profileFile)
    const success = await api.putAdminProfileFile(formData, 'demand', demandId)

    if (!success) return false

    handleDemandDataRefresh(demandId)
    return true
  }

  const handleDemandDataRefresh = async (demandId: number) => {
    if (buyerId) {
      getDemandDetails(demandId)
      await searchDemands(parseInt(buyerId))
    }
  }

  return (
    <Fragment {...props}>
      <tr
        className={expandDetails ? 'expandedRow firstExpandedRow' : undefined}
        data-testid={demand.id}
      >
        <td>
          <Status statusType={demand.status} />
        </td>

        <td>
          <strong>{demand.id}</strong>
        </td>

        <td>
          <strong>{demand.osd_name}</strong>
        </td>

        <td>{demand.electric_energy_meter_number}</td>
        <td>
          {demand.yearly_power_consumption} {copy.common.MWh}
        </td>
        <td>{toCurrency(demand.current_energy_price)}</td>

        <td>
          <Button
            secondary={expandDetails}
            small
            style={{ minWidth: 85, marginLeft: 'auto' }}
            onClick={() => handleDemandDetailsClick(demand.id)}
          >
            {loading ? <Spinner size={16} /> : expandDetails ? button.roll : button.unroll}
          </Button>
        </td>
      </tr>

      {demandDetails && (
        <>
          <tr className={expandDetails ? 'expandedRow' : 'hiddenRow'}>
            <td colSpan={7}>
              <DetailsWrapper>
                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.demand_location}</span>
                    <span>{`${formatZipCode(demandDetails.location_zip_code)} ${
                      demandDetails.location_city
                    }`}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.current_energy_provider_name}</span>
                    <span>{demandDetails.current_energy_provider_name || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_point_number}</span>
                    <span>{demandDetails.energy_point_number || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_tariff_group}</span>
                    <span>{demandDetails.energy_tariff_group}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_multiplier}</span>
                    <span>{`x${demandDetails.energy_multiplier}`}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.contract_length_demand}</span>
                    <span>
                      {demandDetails.contract_length} {copy.common.months_abbr}
                    </span>
                  </Detail>
                </DetailsWrapper>

                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.current_contract_end_date}</span>
                    <span>
                      {!demandDetails.current_contract_end_date
                        ? '-'
                        : formatDate(demandDetails.current_contract_end_date, {
                            options: {
                              day: 'numeric',
                              month: 'numeric',
                              year: 'numeric'
                            }
                          })}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.contract_start_date_demand}</span>
                    <span>
                      {formatDate(demandDetails.contract_start_date, {
                        options: {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric'
                        }
                      })}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.created_date}</span>
                    <span>{formatDate(demandDetails.created_date)}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.last_modified_date}</span>
                    <span>{formatDate(demandDetails.last_modified_date)}</span>
                  </Detail>
                </DetailsWrapper>
              </DetailsWrapper>

              <DetailsWrapper style={{ marginTop: 24 }}>
                <Detail>
                  <h3>{texts.subheading_profile_file}</h3>

                  <DownloadWrapper>
                    <Detail>
                      <span>{texts.original_profile_file}</span>

                      {demandDetails.has_original_profile_file ? (
                        <ProfileFileDownloadChangeWrapper>
                          <Button
                            secondary
                            small
                            onClick={() => handleDownloadProfileFile('original', demandDetails.id)}
                          >
                            <DownloadIcon />
                            {button.download}
                          </Button>
                          <ButtonLink onClick={() => setUpdateProfileFileModalOpen(true)}>
                            {profile_file_text.change_file}
                          </ButtonLink>
                        </ProfileFileDownloadChangeWrapper>
                      ) : (
                        <Button
                          type="button"
                          small
                          onClick={() => setUpdateProfileFileModalOpen(true)}
                        >
                          <PlusIcon />
                          {button.add}
                        </Button>
                      )}
                    </Detail>

                    {demandDetails.has_original_profile_file && (
                      <Detail>
                        <div>
                          <span>{texts.processed_profile_file}</span>
                        </div>

                        {!demandDetails.has_processed_profile_file &&
                          demandDetails.status !== 'DELETED' && (
                            <Button
                              small
                              onClick={() =>
                                navigate(`${file_upload}/${demandDetails.id}/${format_choice}`)
                              }
                            >
                              <PlusIcon />
                              {button.add}
                            </Button>
                          )}

                        {demandDetails.has_processed_profile_file && (
                          <Button
                            secondary
                            small
                            onClick={() => handleDownloadProfileFile('processed', demandDetails.id)}
                          >
                            <DownloadIcon />
                            {button.download}
                          </Button>
                        )}
                      </Detail>
                    )}

                    {demandDetails.has_processed_profile_file &&
                      demandDetails.status === 'ACTIVE' && (
                        <Detail>
                          <button
                            className="green text"
                            onClick={() => setConsumptionDetailsModalOpen(true)}
                            style={{ marginLeft: -45 }}
                          >
                            {texts.button.consumption_details}
                          </button>
                        </Detail>
                      )}
                  </DownloadWrapper>
                </Detail>

                <Detail>
                  <h3>{texts.subheading_status}</h3>
                  <Statuses>
                    <Status statusType={demandDetails.status} />
                    {demandDetails?.status_history?.length > 0 && (
                      <ButtonLink onClick={() => setStatusHistoryModalOpen(true)}>
                        {texts.status_history}
                      </ButtonLink>
                    )}
                  </Statuses>

                  {demandDetails.has_energy_profile && (
                    <>
                      {demandDetails.status === 'INACTIVE' && (
                        <Button
                          small
                          style={{ maxWidth: 126 }}
                          onClick={() => handleDemandActivation(demandDetails.id, 'activate')}
                        >
                          <PowerIcon />
                          {button.activate}
                        </Button>
                      )}

                      {(demandDetails.status === 'ACTIVE' ||
                        demandDetails.status === 'ACCEPTED') && (
                        <Button
                          small
                          secondary
                          warning
                          style={{ maxWidth: 126 }}
                          onClick={() => setTurnOffDemandModalOpen(true)}
                        >
                          <PowerIcon />
                          {button.turn_off}
                        </Button>
                      )}
                    </>
                  )}

                  {!demandDetails.has_energy_profile && demandDetails.status !== 'DELETED' && (
                    <DemandInfo>
                      <InfoIcon />
                      <span>{texts.info}</span>
                    </DemandInfo>
                  )}
                </Detail>

                <ActionButtons>
                  {demandDetails.status === 'INACTIVE' && (
                    <Detail style={{ marginLeft: 'auto' }}>
                      <Button
                        small
                        secondary
                        style={{ marginTop: 'auto', height: 38 }}
                        onClick={() =>
                          handleEditDemandClick(demandDetails.has_processed_profile_file)
                        }
                      >
                        <PencilIcon />
                        {button.edit}
                      </Button>
                    </Detail>
                  )}

                  {demandDetails.status !== 'DELETED' && demandDetails.status !== 'CLOSED' && (
                    <Detail style={{ marginLeft: 'auto' }}>
                      <DeleteButton
                        onClick={() => setDeleteDemandModalOpen(true)}
                        style={{ marginTop: 'auto' }}
                      >
                        {button.delete}
                      </DeleteButton>
                    </Detail>
                  )}
                </ActionButtons>
              </DetailsWrapper>

              <DetailsWrapper style={{ marginTop: 24 }}>
                <Detail>
                  {demandDetails.status !== 'INACTIVE' && demandDetails.status !== 'DELETED' && (
                    <>
                      <h3>{texts.subheading_matching_offers}</h3>
                      <MatchingButtons>
                        {demandDetails.status === 'ACTIVE' && (
                          <>
                            <Button
                              onClick={() => navigate(addParamToPath(matching, demandDetails.id))}
                            >
                              {texts.button.search}
                            </Button>

                            <Button
                              secondary
                              onClick={() =>
                                navigate(addParamToPath(active_offers, demandDetails.id))
                              }
                            >
                              {texts.button.compare}
                            </Button>
                          </>
                        )}

                        <Button
                          secondary
                          onClick={() =>
                            navigate(adminPanelPaths.matches_search, { state: demandDetails.id })
                          }
                        >
                          {texts.button.previous}
                        </Button>
                      </MatchingButtons>
                    </>
                  )}
                </Detail>
              </DetailsWrapper>
            </td>
          </tr>

          {/* Turn off demand */}
          <ConfirmationWarnModal
            title={texts.modal.turn_off.title}
            description={texts.modal.turn_off.description}
            isModalOpen={turnOffDemandModalOpen}
            onModalClose={() => setTurnOffDemandModalOpen(false)}
            onModalConfirmation={() => {
              setTurnOffDemandReasonModalOpen(true)
              setTurnOffDemandModalOpen(false)
            }}
            confirmButtonLabel={button.turn_off}
            closeButtonLabel={button.close}
          />

          <DeleteResourceModal
            title={texts.modal.turn_off_reason.title}
            status={demandDetails.status}
            resourceId={demandDetails.id}
            isModalOpen={turnOffDemandReasonModalOpen}
            onModalClose={() => setTurnOffDemandReasonModalOpen(false)}
            onModalConfirmation={(reason: string) =>
              handleDemandActivation(demandDetails.id, 'deactivate', reason)
            }
            confirmButtonLabel={button.turn_off}
            closeButtonLabel={button.cancel}
          />

          {/* Delete demand */}
          <ConfirmationWarnModal
            title={texts.modal.delete.title}
            description={texts.modal.delete.description}
            isModalOpen={deleteDemandModalOpen}
            onModalClose={() => setDeleteDemandModalOpen(false)}
            onModalConfirmation={() => {
              setDeleteDemandReasonModalOpen(true)
              setDeleteDemandModalOpen(false)
            }}
            confirmButtonLabel={button.delete}
            closeButtonLabel={button.close}
          />

          <DeleteResourceModal
            title={texts.modal.delete_reason.title}
            status={demandDetails.status}
            resourceId={demandDetails.id}
            isModalOpen={deleteDemandReasonModalOpen}
            onModalClose={() => setDeleteDemandReasonModalOpen(false)}
            onModalConfirmation={(reason: string) => handleDemandDelete(demandDetails.id, reason)}
            confirmButtonLabel={button.delete}
            closeButtonLabel={button.cancel}
          />

          {/* Confirmation edit demand data */}
          <ConfirmationEditResourceModal
            clientType="buyer"
            isModalOpen={confirmationEditModal}
            onModalClose={() => setConfirmationEditModal(false)}
            onModalConfirmation={() => {
              setConfirmationEditModal(false)
              setEditDemandDetailsModalOpen(true)
            }}
          />

          {/* Edit demand data */}
          <EditDemandDetailsModal
            demandData={demandDetails}
            isModalOpen={editDemandDetailsModalOpen}
            onModalClose={() => setEditDemandDetailsModalOpen(false)}
            refreshData={() => handleDemandDataRefresh(demandDetails.id)}
          />

          {/* Status history */}
          <StatusHistoryModal
            statusHistory={demandDetails.status_history}
            isModalOpen={statusHistoryModalOpen}
            onModalClose={() => setStatusHistoryModalOpen(false)}
            closeButtonLabel={button.close}
          />

          {/* Consumption details */}
          <ConsumptionDetailsModal
            demandId={demandDetails.id.toString()}
            isModalOpen={consumptionDetailsModalOpen}
            onModalClose={() => setConsumptionDetailsModalOpen(false)}
          />

          {/* Update profile file modal */}
          <UpdateProfileFileModal
            title={
              demandDetails.has_original_profile_file
                ? demandDetails.status === 'ACTIVE'
                  ? profile_file_text.confirm_change_profile_file_deactivate_demand
                  : profile_file_text.confirm_change_profile_file
                : profile_file_text.confirm_add_profile_file
            }
            description={
              demandDetails.has_original_profile_file
                ? demandDetails.status === 'ACTIVE'
                  ? 'change_and_deactivate_demand'
                  : 'change_only'
                : ''
            }
            fieldErrorName="upsert_original_profile_file.original_profile_file"
            isModalOpen={updateProfileFileModalOpen}
            onModalClose={() => setUpdateProfileFileModalOpen(false)}
            onModalConfirmation={(file: File | null, reason: string) =>
              handleUpdateProfileFile(demandDetails.id, file, reason)
            }
          />
        </>
      )}
    </Fragment>
  )
}

const DownloadWrapper = styled.div`
  display: flex;
  gap: 30px;
`

const DemandInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font: var(--400-13);
`

const MatchingButtons = styled.div`
  display: flex;
  gap: 32px;
`

const Statuses = styled.div`
  display: flex;
  gap: 24px;
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-left: auto;
`

const ProfileFileDownloadChangeWrapper = styled.div`
  display: flex;
  justify-content: center;
`
